import { Box, Flex, InputGroup, InputRightElement, NumberInput, NumberInputField } from "@chakra-ui/react";
import React, { useCallback } from "react";
import { Typography } from "@reconvert/react-ui-component";
import { CloseFilled } from "@reconvert/react-ui-component/icons";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../app/hooks";
import { selectAppState } from "../../../../app/slice/appSlice";
import { ConditionFieldErrors } from "../../../../utils/validateConditionData";

interface CartOrderSelectorProps {
  conditionData: any;
  updateConditionData: (val: { value?: string; fromValue?: string; toValue?: string }) => void;
  isOnlyPositiveValue?: boolean;
  roundUpValue?: number;
  fieldErrors?: ConditionFieldErrors;
}

const CartOrderSelector = ({ conditionData, updateConditionData, isOnlyPositiveValue = false, roundUpValue = 0, fieldErrors = {} }: CartOrderSelectorProps) => {

  const { t } = useTranslation();
  const { storeInfo } = useAppSelector(selectAppState);

  const validateDecimalPlaces = useCallback((val: string) => {
    if (!roundUpValue || !val) return val;

    const decimalIndex = val.indexOf('.');

    if (decimalIndex === -1) return val;

    const decimalPlaces = val.length - decimalIndex - 1;

    if (decimalPlaces <= roundUpValue) {
      return val;
    }

    return undefined;
  }, [roundUpValue]);

  const renderFieldError = (field: string) => {
    return fieldErrors?.[field] && (
      <Flex alignItems="center" gap="4px">
        <CloseFilled width="12px" height="12px" color="error.700" />
        <Typography variant="small" color="red.500" mt={0.5}>
          {fieldErrors?.[field]}
        </Typography>
      </Flex>
    );
  };

  if (conditionData?.operator === "between" || conditionData?.operator === "notBetween") {
    return (
      <Box mt={2}>
        <Typography color={"#6A7381"} width={"max-content"} bg={"var(--Natural-100, #F9FAFC)"} variant="small">
          {t("Value")}
        </Typography>
        <Flex alignItems={"center"} gap={1}>
          <InputGroup>
            <NumberInput
              value={conditionData?.fromValue}
              onChange={val => {
                const validatedValue = validateDecimalPlaces(val);

                updateConditionData({ fromValue: validatedValue });
              }}
              min={isOnlyPositiveValue ? 0 : undefined}
              precision={roundUpValue ? roundUpValue : undefined}
              keepWithinRange={false}
              clampValueOnBlur={false}
              isInvalid={(!!conditionData?.fromValue && isOnlyPositiveValue && Number(conditionData?.fromValue) < 0) || !!fieldErrors?.['fromValue']}
            >
              <NumberInputField color={"#1A202C"} _invalid={{ borderColor: "red.500" }} />
            </NumberInput>
            <InputRightElement>{storeInfo?.currencySymbol}</InputRightElement>
          </InputGroup>
          <Typography color={"#6A7381"} width={"max-content"} bg={"var(--Natural-100, #F9FAFC)"} variant="small">
            {t("and")}
          </Typography>
          <InputGroup>
            <NumberInput
              value={conditionData?.toValue}
              onChange={val => {
                const validatedValue = validateDecimalPlaces(val);

                updateConditionData({ toValue: validatedValue });
              }}
              min={isOnlyPositiveValue ? 0 : undefined}
              precision={roundUpValue ? roundUpValue : undefined}
              keepWithinRange={false}
              clampValueOnBlur={false}
              isInvalid={(!!conditionData?.toValue && isOnlyPositiveValue && Number(conditionData?.toValue) < 0) || !!fieldErrors?.['toValue']}
            >
              <NumberInputField color={"#1A202C"} _invalid={{ borderColor: "red.500" }} />
            </NumberInput>
            <InputRightElement>{storeInfo?.currencySymbol}</InputRightElement>
          </InputGroup>
        </Flex>
        <Flex alignItems="center" gap={'14px'} >
          <Box width={'50%'}>
            {renderFieldError('fromValue')}
          </Box>
          {renderFieldError('toValue')}
        </Flex>
      </Box>
    );
  } else {
    return (
      <Box mt={2}>
        <Flex gap={1} flexDirection={"column"}>
          <Typography color={"#6A7381"} width={"max-content"} bg={"var(--Natural-100, #F9FAFC)"} variant="small">
            {t("Value")}
          </Typography>
          <InputGroup>
            <NumberInput
              width={"100%"}
              value={conditionData?.value}
              onChange={val => {
                const validatedValue = validateDecimalPlaces(val);

                updateConditionData({ value: validatedValue });
              }}
              min={isOnlyPositiveValue ? 0 : undefined}
              precision={roundUpValue ? roundUpValue : undefined}
              keepWithinRange={false}
              clampValueOnBlur={false}
              isInvalid={(!!conditionData?.value && isOnlyPositiveValue && Number(conditionData?.value) < 0) || !!fieldErrors?.['value']}
            >
              <NumberInputField color={"#1A202C"} _invalid={{ borderColor: "red.500" }} />
            </NumberInput>
            <InputRightElement>{storeInfo?.currencySymbol}</InputRightElement>
          </InputGroup>
          {renderFieldError('value')}
        </Flex>
      </Box>
    );
  }
};

export default CartOrderSelector;
